import { useState } from "react";
import { FileText, Clock, CheckCircle2, AlertCircle, Plus, ChevronDown, ChevronUp, ExternalLink, Upload } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { MOCK_APPLICATIONS, Application, AppStatus } from "../../data/mockData";

const STATUS_CONFIG: Record<AppStatus, { label: string; color: string; bg: string; border: string; step: number }> = {
  draft:          { label: "Draft",          color: "text-slate-600",  bg: "bg-slate-50",   border: "border-slate-200", step: 0 },
  submitted:      { label: "Submitted",      color: "text-blue-700",   bg: "bg-blue-50",    border: "border-blue-200",  step: 1 },
  under_review:   { label: "Under Review",   color: "text-amber-700",  bg: "bg-amber-50",   border: "border-amber-200", step: 2 },
  offer_received: { label: "Offer Received", color: "text-green-700",  bg: "bg-green-50",   border: "border-green-200", step: 3 },
  accepted:       { label: "Accepted",       color: "text-violet-700", bg: "bg-violet-50",  border: "border-violet-200",step: 4 },
  rejected:       { label: "Not Successful", color: "text-red-700",    bg: "bg-red-50",     border: "border-red-200",   step: -1 },
};

const STEPS = ["Draft", "Submitted", "Under Review", "Offer Received", "Accepted"];

const TABS = ["All", "Local", "Overseas", "Active", "Completed"];

function ProgressTracker({ status }: { status: AppStatus }) {
  const currentStep = STATUS_CONFIG[status].step;
  return (
    <div className="flex items-center gap-1 flex-1">
      {STEPS.map((step, i) => (
        <div key={i} className="flex items-center flex-1">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] flex-shrink-0 transition-all ${
            i < currentStep ? "bg-violet-600 text-white" :
            i === currentStep ? "bg-gradient-to-br from-violet-600 to-purple-600 text-white shadow-md shadow-violet-500/30" :
            "bg-slate-100 text-slate-400"
          }`} style={{ fontWeight: 700 }}>
            {i < currentStep ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
          </div>
          {i < STEPS.length - 1 && (
            <div className={`flex-1 h-0.5 mx-1 rounded-full transition-all ${i < currentStep ? "bg-violet-500" : "bg-slate-200"}`} />
          )}
        </div>
      ))}
    </div>
  );
}

function ApplicationCard({ app }: { app: Application }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = STATUS_CONFIG[app.status];
  const docPercent = app.totalDocs > 0 ? Math.round((app.completedDocs / app.totalDocs) * 100) : 0;

  return (
    <motion.div layout className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden hover:shadow-md transition-all duration-300">
      <div className="p-5 lg:p-6">
        <div className="flex items-start gap-4">
          {/* Flag */}
          <div className="w-12 h-12 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-[24px] flex-shrink-0 shadow-sm">
            {app.flag}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 mb-1">
              <div className="min-w-0">
                <h3 className="text-[15px] text-slate-900 truncate" style={{ fontWeight: 700 }}>{app.university}</h3>
                <p className="text-[12px] text-slate-500 truncate" style={{ fontWeight: 400 }}>{app.course}</p>
              </div>
              <div className="flex items-center flex-shrink-0 gap-2">
                <span className={`flex items-center gap-1 px-3 py-1 rounded-full border text-[11px] ${cfg.color} ${cfg.bg} ${cfg.border}`} style={{ fontWeight: 700 }}>
                  {app.status === "offer_received" && <CheckCircle2 className="w-3 h-3" />}
                  {app.status === "under_review" && <Clock className="w-3 h-3" />}
                  {app.status === "draft" && <FileText className="w-3 h-3" />}
                  {cfg.label}
                </span>
              </div>
            </div>

            {/* Progress tracker */}
            {app.status !== "rejected" && (
              <div className="mt-3 mb-2">
                <ProgressTracker status={app.status} />
                <div className="flex justify-between mt-1.5">
                  {STEPS.map((s, i) => (
                    <span key={i} className={`text-[9px] text-center leading-tight ${i === STATUS_CONFIG[app.status].step ? "text-violet-600" : "text-slate-400"}`}
                      style={{ fontWeight: i === STATUS_CONFIG[app.status].step ? 700 : 400, flex: 1 }}>
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Key info row */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
              <span className="text-[11px] text-slate-500" style={{ fontWeight: 400 }}>
                📅 Intake: <span className="text-slate-700" style={{ fontWeight: 600 }}>{app.intake}</span>
              </span>
              <span className="text-[11px] text-slate-500" style={{ fontWeight: 400 }}>
                ⏰ Deadline: <span className="text-slate-700" style={{ fontWeight: 600 }}>{app.deadline}</span>
              </span>
              {app.scholarshipAmount && app.scholarshipAmount !== "—" && (
                <span className="text-[11px] text-green-600" style={{ fontWeight: 600 }}>
                  🎓 {app.scholarshipAmount}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Expand button */}
        <button onClick={() => setExpanded(!expanded)}
          className="mt-4 w-full flex items-center justify-between px-4 py-2.5 rounded-2xl bg-slate-50 hover:bg-slate-100 transition-colors text-[12px] text-slate-600"
          style={{ fontWeight: 600 }}>
          <span>{expanded ? "Hide details" : "View details & documents"}</span>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Expanded details */}
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }} className="overflow-hidden">
            <div className="px-5 lg:px-6 pb-6 border-t border-slate-50 pt-4">
              <div className="grid sm:grid-cols-2 gap-5">
                {/* Info */}
                <div>
                  <p className="text-[12px] text-slate-500 uppercase tracking-wider mb-3" style={{ fontWeight: 700 }}>Application Details</p>
                  <div className="flex flex-col gap-2.5">
                    {[
                      { label: "Applied On", value: app.appliedAt },
                      { label: "Tuition", value: app.tuition },
                      { label: "Scholarship", value: app.scholarshipAmount },
                      { label: "Type", value: app.type === "local" ? "🇲🇾 Local" : "🌍 Overseas" },
                    ].map(item => (
                      <div key={item.label} className="flex justify-between items-center">
                        <span className="text-[12px] text-slate-500" style={{ fontWeight: 500 }}>{item.label}</span>
                        <span className="text-[12px] text-slate-800" style={{ fontWeight: 600 }}>{item.value}</span>
                      </div>
                    ))}
                  </div>
                  {app.notes && (
                    <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-100">
                      <p className="text-[11px] text-amber-700 flex gap-1.5" style={{ fontWeight: 500 }}>
                        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        {app.notes}
                      </p>
                    </div>
                  )}
                </div>

                {/* Documents */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[12px] text-slate-500 uppercase tracking-wider" style={{ fontWeight: 700 }}>Documents</p>
                    <span className="text-[11px] text-slate-600" style={{ fontWeight: 600 }}>{app.completedDocs}/{app.totalDocs}</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5 mb-3">
                    <div className="h-1.5 rounded-full bg-gradient-to-r from-violet-500 to-purple-500 transition-all duration-500" style={{ width: `${docPercent}%` }} />
                  </div>
                  <div className="flex flex-col gap-2">
                    {["IC / Passport", "SPM Results", "Personal Statement", "Recommendation Letter", "Bank Statement"].slice(0, app.totalDocs).map((doc, i) => (
                      <div key={i} className={`flex items-center gap-2.5 p-2.5 rounded-xl ${i < app.completedDocs ? "bg-green-50" : "bg-slate-50"}`}>
                        {i < app.completedDocs ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" /> : <Upload className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
                        <span className={`text-[12px] ${i < app.completedDocs ? "text-green-700" : "text-slate-500"}`} style={{ fontWeight: i < app.completedDocs ? 500 : 400 }}>{doc}</span>
                        {i >= app.completedDocs && <button className="ml-auto text-[11px] text-violet-600 hover:text-violet-700" style={{ fontWeight: 600 }}>Upload</button>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3 mt-5 pt-4 border-t border-slate-100">
                {app.status === "offer_received" && (
                  <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white text-[13px] shadow-md hover:shadow-lg transition-all hover:-translate-y-px" style={{ fontWeight: 600 }}>
                    <CheckCircle2 className="w-4 h-4" /> Accept Offer
                  </button>
                )}
                <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-[13px] hover:bg-slate-50 transition-all" style={{ fontWeight: 600 }}>
                  <ExternalLink className="w-4 h-4" /> View University
                </button>
                <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-violet-200 text-violet-700 bg-violet-50 text-[13px] hover:bg-violet-100 transition-all" style={{ fontWeight: 600 }}>
                  💬 Ask Counselor
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function ApplicationsPage() {
  const [activeTab, setActiveTab] = useState("All");

  const filtered = MOCK_APPLICATIONS.filter(app => {
    if (activeTab === "Local") return app.type === "local";
    if (activeTab === "Overseas") return app.type === "overseas";
    if (activeTab === "Active") return !["accepted", "rejected"].includes(app.status);
    if (activeTab === "Completed") return ["accepted", "rejected"].includes(app.status);
    return true;
  });

  return (
    <div className="p-4 lg:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <h1 className="text-[28px] text-slate-900 tracking-tight" style={{ fontWeight: 900 }}>Applications</h1>
        <p className="text-[14px] text-slate-500 mt-1" style={{ fontWeight: 400 }}>
          Track every application in real-time — from draft to accepted.
        </p>
      </motion.div>

      {/* Stats */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
        className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Total", value: MOCK_APPLICATIONS.length, color: "text-slate-900" },
          { label: "In Progress", value: MOCK_APPLICATIONS.filter(a => ["submitted","under_review"].includes(a.status)).length, color: "text-blue-600" },
          { label: "Offers", value: MOCK_APPLICATIONS.filter(a => a.status === "offer_received").length, color: "text-green-600" },
          { label: "Drafts", value: MOCK_APPLICATIONS.filter(a => a.status === "draft").length, color: "text-slate-500" },
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 text-center">
            <p className={`text-[28px] ${stat.color}`} style={{ fontWeight: 900 }}>{stat.value}</p>
            <p className="text-[12px] text-slate-500" style={{ fontWeight: 500 }}>{stat.label}</p>
          </div>
        ))}
      </motion.div>

      {/* Tabs + Add button */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-1 bg-white rounded-2xl border border-slate-100 p-1.5 shadow-sm overflow-x-auto">
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-xl text-[13px] whitespace-nowrap transition-all ${activeTab === tab ? "bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"}`}
              style={{ fontWeight: activeTab === tab ? 700 : 500 }}>
              {tab}
            </button>
          ))}
        </div>
        <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[13px] shadow-md hover:shadow-lg transition-all hover:-translate-y-px flex-shrink-0" style={{ fontWeight: 600 }}>
          <Plus className="w-4 h-4" /> New Application
        </button>
      </motion.div>

      {/* Application cards */}
      <div className="flex flex-col gap-4">
        {filtered.map((app, i) => (
          <motion.div key={app.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
            <ApplicationCard app={app} />
          </motion.div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16">
          <div className="text-[48px] mb-4">📋</div>
          <p className="text-[16px] text-slate-900 mb-2" style={{ fontWeight: 700 }}>No applications here</p>
          <p className="text-[13px] text-slate-500" style={{ fontWeight: 400 }}>Switch tabs or start a new application.</p>
        </div>
      )}
    </div>
  );
}
